import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Mail, Lock, LogIn, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '../../lib/apiErrors';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { LegalLinks } from '../../components/LegalLinks';
import { isNativeApp } from '../../lib/platform';

// Logo URL
const LOGO_URL = "/logo.jpeg";

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login({ email, password });
      toast.success(t('auth.loginSuccess'));
      navigate('/home');
    } catch (err) {
      // Handle Pydantic validation errors (array of objects) or simple string detail
      const errorMsg = apiErrorMessage(err, t, 'auth.loginFailed');
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628] flex flex-col">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-teal-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <header className="relative z-10 p-6 flex justify-between items-center">
        {isNativeApp() ? (
          <span />
        ) : (
          <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-teal-400 transition-colors" data-testid="back-link">
            <ArrowLeft className="w-5 h-5" />
            <span>{t('common.back')}</span>
          </Link>
        )}
        <LanguageSwitcher />
      </header>

      {/* Login Form */}
      <div className="flex-1 flex items-center justify-center px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full shadow-lg shadow-teal-500/30 overflow-hidden border-2 border-teal-400/30 mx-auto mb-4">
              <img src={LOGO_URL} alt="SocraQuest" className="w-full h-full object-cover" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
              {t('auth.welcome_back')}
            </h1>
            <p className="text-slate-400 mt-2">{t('auth.sign_in_continue')}</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Alert */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-sm flex items-center gap-2" data-testid="login-error">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500"
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 h-12 text-lg font-semibold"
              data-testid="login-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">@</span>
                  {t('auth.loggingIn')}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LogIn className="w-5 h-5" />
                  {t('auth.login')}
                </span>
              )}
            </Button>
          </form>

          {/* Register Link */}
          <p className="text-center mt-6 text-slate-400">
            {t('auth.noAccount')}{' '}
            <Link to="/register" className="text-teal-400 hover:text-teal-300 font-medium">
              {t('auth.register')}
            </Link>
          </p>
          <LegalLinks className="mt-8" />
        </motion.div>
      </div>
    </div>
  );
}
